/**
 * ControlPanel API (`/phrase-templates`) クライアント (Issue #449 / SBI-05・SBI-07)。
 *
 * 放送文面の「基本文」を言語別に保持する定型文マスタ (PhraseTemplateMaster) を CRUD する。
 * 受け入れ条件「基本文に近い文章は定型文マスタを用いること」
 * 「定型マスターは OBA-Core で編集できること」に対応する。
 *
 * - すべての呼び出しに `Authorization: Bearer <Cognito ID Token>` を付与する。
 * - エンドポイントは `AppConfig.apiUrl` をベースとする。
 * - 定型文は `(template_id, language_code)` の複合キーで一意なので、個別アイテムの
 *   パスは 2 階層 (`phrase-templates/{template_id}/{language_code}`) になる。
 *
 * ⚠️ `language_code` に渡す値は Polly の `LanguageCode` (`ja-JP`) ではなく
 *    **language_key** (`ja` / `zh`) を使うこと。API 側でも `ja-JP` は `ja` へ
 *    正規化されるが、フロントでは `POLLY_LANGUAGE_OPTIONS` のキーをそのまま使う。
 */
import type { AppConfig } from '../config';

import { ApiError, joinUrl } from './common';
import type {
  ApiErrorBody,
  PhraseTemplateCreateRequest,
  PhraseTemplateDeleteResponse,
  PhraseTemplateListResponse,
  PhraseTemplateResetResponse,
  PhraseTemplateSaveResult,
  PhraseTemplateUpdateRequest,
} from './types';

export interface PhraseTemplatesClientOptions {
  /** Cognito ID トークンを取得する関数 (期限切れ時は AuthProvider 側で自動更新済み) */
  getIdToken: () => Promise<string>;
  /** AppConfig (apiUrl 取得用) */
  config: AppConfig;
  /** テスト時に差し込む fetch 実装 */
  fetchImpl?: typeof fetch;
}

export class PhraseTemplatesClient {
  private readonly options: PhraseTemplatesClientOptions;

  constructor(options: PhraseTemplatesClientOptions) {
    this.options = options;
  }

  /**
   * GET /phrase-templates (定型文マスタ一覧)
   *
   * 一覧は `template_id` 昇順 → **マスタ定義の言語順** (日→英→中→韓…) で返る。
   * 表示順が放送順序と一致するため、フロント側で並べ替える必要はない。
   *
   * @param templateId 指定すると当該種別のみに絞り込む (省略時は全件)
   * @param options `includeDefaults: true` のとき、未登録の種別についても
   *   **コード内既定の日本語文面** を `body` として同梱する (Issue #527)。
   *   未登録行を空欄で表示すると「空欄で保存 → 意図せず既定に戻る」事故になるため、
   *   定型文マスタ編集画面はこれを使う。返る要素には `is_default` が付く。
   */
  async list(
    templateId?: string,
    options?: { includeDefaults?: boolean },
  ): Promise<PhraseTemplateListResponse> {
    const params = new URLSearchParams();
    if (typeof templateId === 'string' && templateId.trim() !== '') {
      params.set('template_id', templateId.trim());
    }
    if (options?.includeDefaults) {
      params.set('include_defaults', 'true');
    }
    const query = params.size > 0 ? `?${params.toString()}` : '';
    return this.request<PhraseTemplateListResponse>('GET', `phrase-templates${query}`);
  }

  /**
   * POST /phrase-templates (定型文を登録)
   *
   * 同一 `(template_id, language_code)` が既に存在する場合は **上書き** される
   * (冪等な upsert。管理画面の「保存」を何度押しても同じ結果になる)。
   */
  async create(params: {
    templateId: string;
    languageCode: string;
    body: string;
    label?: string;
  }): Promise<PhraseTemplateSaveResult> {
    const payload: PhraseTemplateCreateRequest = {
      template_id: params.templateId,
      language_code: params.languageCode,
      body: params.body,
    };
    if (typeof params.label === 'string' && params.label !== '') {
      payload.label = params.label;
    }
    return this.request<PhraseTemplateSaveResult>('POST', 'phrase-templates', payload);
  }

  /**
   * PUT /phrase-templates/{template_id}/{language_code} (定型文を更新)
   *
   * `body` / `label` のうち指定したものだけを更新する (未指定は現在値を維持)。
   * `label` に空文字を渡すと表示名をクリアする。
   */
  async update(
    templateId: string,
    languageCode: string,
    params: { body?: string; label?: string },
  ): Promise<PhraseTemplateSaveResult> {
    const payload: PhraseTemplateUpdateRequest = {};
    if (typeof params.body === 'string') {
      payload.body = params.body;
    }
    if (typeof params.label === 'string') {
      payload.label = params.label;
    }
    return this.request<PhraseTemplateSaveResult>(
      'PUT',
      this.itemPath(templateId, languageCode),
      payload,
    );
  }

  /** DELETE /phrase-templates/{template_id}/{language_code} (定型文を削除) */
  async remove(templateId: string, languageCode: string): Promise<PhraseTemplateDeleteResponse> {
    return this.request<PhraseTemplateDeleteResponse>(
      'DELETE',
      this.itemPath(templateId, languageCode),
    );
  }

  /**
   * DELETE /phrase-templates (定型文マスタの初期化 / Issue #631)
   *
   * 登録済みの定型文を **すべて削除** し、コード内既定文面へ戻す。
   * `templateId` を指定すると当該種別 (全言語) のみ初期化する。
   */
  async reset(templateId?: string): Promise<PhraseTemplateResetResponse> {
    const params = new URLSearchParams();
    if (typeof templateId === 'string' && templateId.trim() !== '') {
      params.set('template_id', templateId.trim());
    }
    const query = params.size > 0 ? `?${params.toString()}` : '';
    return this.request<PhraseTemplateResetResponse>('DELETE', `phrase-templates${query}`);
  }

  // ---------------------------------------------------------------------
  // 内部実装
  // ---------------------------------------------------------------------

  /** 個別アイテムのパス (2 階層のパスパラメータ) を組み立てる。 */
  private itemPath(templateId: string, languageCode: string): string {
    return `phrase-templates/${encodeURIComponent(templateId)}/${encodeURIComponent(languageCode)}`;
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = joinUrl(this.options.config.apiUrl, path);
    const idToken = await this.options.getIdToken();
    const fetchImpl = this.options.fetchImpl ?? fetch;

    const res = await fetchImpl(url, {
      method,
      headers: {
        ...(body !== undefined && { 'Content-Type': 'application/json' }),
        Authorization: `Bearer ${idToken}`,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!res.ok) {
      let errorBody: ApiErrorBody | undefined;
      try {
        errorBody = (await res.json()) as ApiErrorBody;
      } catch {
        // body が JSON で無い場合は無視
      }
      const message = errorBody?.message ?? `API request failed: HTTP ${res.status}`;
      throw new ApiError(res.status, message, errorBody);
    }

    return (await res.json()) as T;
  }
}
